import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ActionEditIcon } from '../../assets/icons/action-edit.svg';
import { ReactComponent as AddUserIcon } from '../../assets/icons/add-role.svg';
import { ReactComponent as RoleIconActiveDark } from '../../assets/icons/iconsDark/role-icon-active.svg';
import { ReactComponent as RoleIconDark } from '../../assets/icons/iconsDark/role-icon-dark.svg';
import { ReactComponent as UserIconActiveDark } from '../../assets/icons/iconsDark/user-icon-active-dark.svg';
import { ReactComponent as UserIconDark } from '../../assets/icons/iconsDark/user-icon-dark.svg';
import { ReactComponent as ActiveRoleIcon } from '../../assets/icons/role-active.svg';
import { ReactComponent as RoleIcon } from '../../assets/icons/role.svg';
import { ReactComponent as ActiveUserIcon } from '../../assets/icons/user-active.svg';
import { ReactComponent as UserIcon } from '../../assets/icons/user.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import ThemeContext from '../../contexts/theme-context/ThemeContext';
import { RequestRoleInterFace, RoleInterFace } from '../../interface/role-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import {
  fetchAllRoles,
  setRoleFilterData,
  updateRoleFilters,
  fetchListFilters,
  setSortData,
} from '../../redux/slice/RoleManagementSlice';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import ExportButton from '../../shared-components/export-button/ExportButton';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import PageTitle from '../../shared-components/page-title/PageTitle';
import RoleSidePanel from '../../shared-components/table-side-panel/RoleSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import roleApi from '../../utils/api/roles';

import AddRole from './component/AddRole';
import EditRole from './component/EditRole';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
}
/**
 * Role management function component
 * @returns
 */
function RoleManagement() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { theme } = useContext(ThemeContext);
  const { roles, page, limit, totalPages, totalResults, loading, filters, filterListLoading } =
    useAppSelector((state) => state.roles);
  const { currentFacilityTagsList } = useAppSelector((state) => state.facilityTags);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const userList = ['User', 'Role'];
  /**
   * getIcons
   */
  const getIcons = () => ({
    defaultIcons: [
      theme === 'light' ? <UserIcon /> : <UserIconDark />,
      theme === 'light' ? <RoleIcon /> : <RoleIconDark />,
    ],
    activeIcons: [
      theme === 'light' ? <ActiveUserIcon /> : <UserIconActiveDark />,
      theme === 'light' ? <ActiveRoleIcon /> : <RoleIconActiveDark />,
    ],
  });
  const { defaultIcons, activeIcons } = getIcons();
  const { sortBy, sortField } = useAppSelector((state) => state.roles);

  /**
   * Handle the fetch data for Api call function
   * @param data
   */
  const fetchData = (data: RequestRoleInterFace) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchAllRoles({
          ...data,
        }),
      );
    }
  };

  /**
   * Handle the submit function
   */
  const submit = () => {
    setModalBoxDetail({
      title: 'Add Role',
      content: <AddRole handleCancel={() => setModalBoxStatus('close')} />,
      notificationModal: false,
      header: true,
    });
    setModalBoxStatus('open');
  };
  /**
   * Handle the Edit Role function
   */
  const handleEditRole = (currentData: RoleInterFace) => {
    setModalBoxDetail({
      title: 'Edit Role',
      content: (
        <EditRole handleCancel={() => setModalBoxStatus('close')} selectedRole={currentData} />
      ),
      notificationModal: false,
      header: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * Handle the sort field function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setRoleFilterData(sortData));
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to handle page change
   * @param pageNumber
   */
  const handlePageChange = (pageNumber: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortField,
      sortBy,
      page: pageNumber,
      limit,
    });
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
    });
  };
  /**
   * Handle the toggle button
   * @param value
   */
  const onToggle = (value: number | string) => {
    if (value === 0) {
      navigate(`../user`);
    }
  };

  const column = [
    {
      label: 'S. No',
      accessorKey: '' as keyof RoleInterFace,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (station: RoleInterFace, index: number) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {index + 1}
        </div>
      ),
      columnWidth: 40,
    },
    {
      label: 'Date',
      accessorKey: 'date' as keyof RoleInterFace,
      sortAllow: true,
    },
    {
      label: 'Role name',
      accessorKey: 'name' as keyof RoleInterFace,
      sortAllow: true,
      queryParamsKey: 'roleId',
      sidePanelComponent: <RoleSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: 'Responsibility',
      accessorKey: 'responsibility' as keyof RoleInterFace,
      sortAllow: true,
    },
    {
      label: 'Actions',
      accessorKey: '' as keyof RoleInterFace,
      columnWidth: 30,
      /**
       * Action button in table
       * @param station
       * @param index
       */
      customData: (station: RoleInterFace) => (
        <div
          className="action-button-group"
          onClick={() => handleEditRole(station)}
          role="presentation">
          <div className="action-button-item view-color">
            <ActionEditIcon />
          </div>
        </div>
      ),
    },
  ];

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    roleApi
      .fetchRolesExport({ ...filters })
      .then((response) => {
        // Convert data to CSV format
        const headers = ['Date', 'Role name', 'Responsibility'];
        const rows = response.data.results.map((row: RoleInterFace) => [
          row.createdAt ? ConvertUTCtoDate(row.createdAt) : '',
          row.name || '',
          row.responsibility || '',
        ]);
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'roles-list.csv');
          } else {
            exportPDF(headers, rows, 'roles-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: DefaultApiLimit.limit,
    });
  }, [currentFacilityTagsList, filters]);

  return (
    <div className="role-management-content">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        header={modalBoxDetail.header}
        notification={modalBoxDetail.notificationModal}
      />
      <div className="role-management-header">
        <PageTitle
          title="User & Role management"
          buttonLabel="Add Role"
          buttonSvgIcon={<AddUserIcon />}
          handleToggle={onToggle}
          icons={defaultIcons}
          labels={['User', 'Role']}
          activeIcons={activeIcons}
          buttonType="back"
          enableToggleButton
          toggleList={userList}
          onClickButton={submit}
          defaultIndex={1}
        />
      </div>
      <div>
        <CustomTable
          paginate
          loading={loading}
          limit={limit}
          sortData={{
            sortBy,
            sortField
          }}
          handleSortChange={handleSortFunction}
          header={column}
          data={roles}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalResults}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          footerLeft={
            <div className="table-bottom-button-list">
              <div className="table-button-item">
                <ExportButton
                  label="Export Data"
                  buttonList={[
                    {
                      key: 'pdf',
                      allow: true,
                      /**
                       * Export pdf function
                       * @returns
                       */
                      onClickFunction: () => exportDataPdfOrCSV('pdf'),
                    },
                    {
                      key: 'csv',
                      allow: true,
                      /**
                       * Export csv function
                       * @returns
                       */
                      onClickFunction: () => exportDataPdfOrCSV('csv'),
                    },
                  ]}
                  disabled={roles.length === 0}
                  loader={exportLoader}
                />
              </div>
            </div>
          }
          topHeader={false}
          filters={filters}
          filtersToDispatch={updateRoleFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
        />
      </div>
    </div>
  );
}

export default RoleManagement;
